import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useHeaderSlot } from '../context/HeaderSlot';
import './Tables.css';

function parseJwt(token) {
  try { return JSON.parse(atob(token.split('.')[1])); } catch { return {}; }
}

const STATUS_LABEL = { free: 'Wolny', occupied: 'Zajęty', reserved: 'Rezerwacja' };
const STATUS_CLASS = { free: 'free', occupied: 'occupied', reserved: 'reserved' };

const prevReadyIds = { current: new Set() };

function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 200].forEach(delay => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 660;
      gain.gain.setValueAtTime(0.25, ctx.currentTime + delay / 1000);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay / 1000 + 0.3);
      osc.start(ctx.currentTime + delay / 1000);
      osc.stop(ctx.currentTime + delay / 1000 + 0.35);
    });
  } catch {}
}

const prevReservationIds = { current: new Set() };

function playReservationBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 180, 360].forEach(delay => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 520;
      gain.gain.setValueAtTime(0.22, ctx.currentTime + delay / 1000);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay / 1000 + 0.25);
      osc.start(ctx.currentTime + delay / 1000);
      osc.stop(ctx.currentTime + delay / 1000 + 0.3);
    });
  } catch {}
}

function formatAge(seconds) {
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export default function Tables() {
  const [tables, setTables]         = useState([]);
  const [readyOrders, setReadyOrders] = useState([]);
  const [showPanel, setShowPanel]   = useState(false);
  const [upcomingReservations, setUpcomingReservations] = useState([]);
  const [showReservationPanel, setShowReservationPanel] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [tick, setTick]             = useState(0);
  const navigate = useNavigate();
  const headerSlot = useHeaderSlot();
  const isFirstLoad = useRef(true);

  const token = localStorage.getItem('cafepos_token');
  const userRole = parseJwt(token)?.role;
  const isTerminal = userRole === 'terminal';

  async function openDrawer() {
    try {
      await fetch('/api/drawer/open', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
    } catch (err) { console.error('Szuflada:', err); }
  }

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('cafepos_token');
      const [tablesData, kitchenRes] = await Promise.all([
        api.getTables(),
        fetch('/api/kitchen/orders/zbiorczy', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setTables(tablesData);
      const kitchenOrders = await kitchenRes.json();

      // Rezerwacje - CAŁKOWICIE odizolowane od reszty. Jeśli trasa /api/reservations
      // jeszcze nie istnieje na backendzie (albo zwróci błąd z jakiegokolwiek innego
      // powodu), to nie może w żaden sposób zepsuć wczytania stolików ani zamówień.
      try {
        const reservationsRes = await fetch('/api/reservations?upcoming=60', { headers: { Authorization: `Bearer ${token}` } });
        if (reservationsRes.ok) {
          const reservations = await reservationsRes.json();
          setUpcomingReservations(reservations);

          // Alert dźwiękowy, gdy pojawi się NOWA rezerwacja w oknie najbliższych 60 minut
          const newReservationIds = new Set(reservations.map(r => r.id));
          const hasNewReservation = [...newReservationIds].some(id => !prevReservationIds.current.has(id));
          if (hasNewReservation && !isFirstLoad.current) playReservationBeep();
          prevReservationIds.current = newReservationIds;
        }
      } catch (resErr) {
        console.error('Rezerwacje (nieblokujące):', resErr);
      }

      // Zamówienia które mają przynajmniej jedną pozycję gotową (ready) i niedostarczoną (pending)
      const ready = kitchenOrders.filter(o =>
        o.items && o.items.some(i => i.item_kitchen_status === 'ready' && i.item_delivery_status === 'pending')
      );
      setReadyOrders(ready);

      // Sprawdź czy pojawiły się nowe gotowe pozycje
      const newReadyItemIds = new Set(
        ready.flatMap(o => o.items
          .filter(i => i.item_kitchen_status === 'ready' && i.item_delivery_status === 'pending')
          .map(i => i.order_item_id)
        )
      );
      const hasNew = [...newReadyItemIds].some(id => !prevReadyIds.current.has(id));
      if (hasNew && !isFirstLoad.current) playBeep();
      prevReadyIds.current = newReadyItemIds;
      isFirstLoad.current = false;
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 8000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  async function markDelivered(orderId) {
    try {
      const token = localStorage.getItem('cafepos_token');
      await fetch(`/api/kitchen/orders/${orderId}/deliver-ready`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ slug: 'zbiorczy' }),
      });
      await load();
      if (readyOrders.filter(o => o.id !== orderId).length === 0) setShowPanel(false);
    } catch (err) {
      console.error(err);
    }
  }

  async function markReservationArrived(reservation) {
    try {
      const token = localStorage.getItem('cafepos_token');
      await fetch(`/api/reservations/${reservation.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'arrived' }),
      });
      await load();
      if (upcomingReservations.filter(r => r.id !== reservation.id).length === 0) setShowReservationPanel(false);
    } catch (err) {
      console.error(err);
    }
  }

  if (loading) return <div className="page-loading">Ładowanie stolików...</div>;

  const readyTableIds = new Set(readyOrders.map(o => o.table_id));
  const occupied = tables.filter(t => t.status === 'occupied').length;

  // Stoliki z niepustym koszykiem w sessionStorage (koszyk lokalny, niezłożone zamówienie)
  const pendingCartTableIds = new Set(
    tables.map(t => t.id).filter(id => {
      try {
        const cart = JSON.parse(sessionStorage.getItem(`cafepos_cart_${id}`) || '[]');
        return cart.length > 0;
      } catch { return false; }
    })
  );

  const summaryContent = (
    <>
      <span className="summary-item occupied-dot">{occupied} zajęte</span>
      <span className="summary-item free-dot">{tables.filter(t => t.status === 'free').length} wolne</span>
      <button
        onClick={() => upcomingReservations.length > 0 && setShowReservationPanel(p => !p)}
        className="summary-item reserved-dot"
        style={{ border: 'none', cursor: upcomingReservations.length > 0 ? 'pointer' : 'default', font: 'inherit' }}
      >
        {upcomingReservations.length} rezerwacje
      </button>
      <button
        onClick={() => navigate('/reservations')}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 14px', background: 'var(--surface)',
          border: '0.5px solid var(--border)', borderRadius: 20,
          cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--text)',
        }}
      >
        📅 Rezerwacje
      </button>
      {readyOrders.length > 0 && (
        <button
          onClick={() => setShowPanel(p => !p)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', background: '#EAF3DE',
            border: '0.5px solid #C0DD97', borderRadius: 20,
            cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#3B6D11',
          }}
        >
          🔔 Gotowe do odbioru
          <span style={{ background: '#3B6D11', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 20 }}>
            {readyOrders.length}
          </span>
        </button>
      )}
      {isTerminal && (
        <button
          onClick={openDrawer}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 14px', background: 'var(--surface)',
            border: '0.5px solid var(--border)', borderRadius: 20,
            cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--text)',
          }}
        >
          🔔 Szuflada
        </button>
      )}
      <button
        onClick={() => navigate('/order/41')}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 14px', background: 'var(--blue)',
          border: 'none', borderRadius: 20,
          cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#fff',
        }}
      >
        ⚡ Szybki rachunek
      </button>
    </>
  );

  return (
    <div className="tables-page">
      {headerSlot
        ? ReactDOM.createPortal(summaryContent, headerSlot)
        : <div className="tables-summary">{summaryContent}</div>}

      <div className="tables-grid">
        {tables.map(table => {
          const isReady = readyTableIds.has(table.id);
          const hasPendingCart = pendingCartTableIds.has(table.id) && table.status === 'free';
          const reservation = upcomingReservations.find(r => r.table_id === table.id);
          const isReserved = !!reservation && table.status === 'free' && !isReady && !hasPendingCart;
          const reservationTime = reservation
            ? new Date(reservation.reservation_time).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
            : null;
          return (
            <div
              key={table.id}
              className={`table-card ${isReady ? 'ready' : hasPendingCart ? 'occupied' : isReserved ? 'reserved' : STATUS_CLASS[table.status]}`}
              onClick={() => navigate(`/order/${table.id}`)}
            >
              {isReady && <div className="ready-pulse" />}
              <div className="table-number">{table.name || table.number}</div>
              <div className="table-seats">{table.seats} os.</div>
              <div className="table-status">
                {isReady ? '🔔 Gotowe!' : hasPendingCart ? '🛒 Koszyk' : isReserved ? `🕐 ${reservationTime}` : STATUS_LABEL[table.status]}
              </div>
              {table.open_total > 0 && (
                <div className="table-total">{Number(table.open_total).toFixed(2)} zł</div>
              )}
            </div>
          );
        })}
      </div>

      {showPanel && (
        <>
          <div
            onClick={() => setShowPanel(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 50,
            }}
          />
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 51,
            background: 'var(--surface)', borderTop: '0.5px solid var(--border)',
            borderRadius: '12px 12px 0 0', maxHeight: '60vh', overflow: 'auto',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px', borderBottom: '0.5px solid var(--border)',
              position: 'sticky', top: 0, background: 'var(--surface)',
            }}>
              <span style={{ fontSize: 15, fontWeight: 500 }}>
                🔔 Gotowe do odbioru
              </span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {readyOrders.length} zamówień
              </span>
            </div>

            {readyOrders.map(order => {
              const age = Math.floor((Date.now() - new Date(order.created_at)) / 1000);
              const tableName = order.table_name || `Stolik ${order.table_number}`;
              const itemSummary = (order.items || [])
                .map(i => `${i.name} ×${i.qty}`)
                .join(', ');
              return (
                <div key={order.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 20px', borderBottom: '0.5px solid var(--border)',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{tableName}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {(order.items || [])
                        .filter(i => i.item_kitchen_status === 'ready' && i.item_delivery_status === 'pending')
                        .map(i => `${i.name} ×${i.qty}`)
                        .join(', ') || itemSummary}
                    </div>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#3B6D11', minWidth: 48, textAlign: 'right' }}>
                    {formatAge(age + tick * 0)}
                  </span>
                  <button
                    onClick={() => markDelivered(order.id)}
                    style={{
                      padding: '8px 16px', background: '#EAF3DE',
                      border: '0.5px solid #C0DD97', borderRadius: 8,
                      fontSize: 13, fontWeight: 500, color: '#3B6D11', cursor: 'pointer',
                    }}
                  >
                    Wydano
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {showReservationPanel && (
        <>
          <div
            onClick={() => setShowReservationPanel(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 50,
            }}
          />
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 51,
            background: 'var(--surface)', borderTop: '0.5px solid var(--border)',
            borderRadius: '12px 12px 0 0', maxHeight: '60vh', overflow: 'auto',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px', borderBottom: '0.5px solid var(--border)',
              position: 'sticky', top: 0, background: 'var(--surface)',
            }}>
              <span style={{ fontSize: 15, fontWeight: 500 }}>
                📅 Nadchodzące rezerwacje (60 min)
              </span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {upcomingReservations.length} rezerwacji
              </span>
            </div>

            {upcomingReservations.map(r => {
              const minutesTo = Math.round((new Date(r.reservation_time) - Date.now()) / 60000);
              const tableName = r.table_name || `Stolik ${r.table_number}`;
              const timeLabel = new Date(r.reservation_time).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
              return (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 20px', borderBottom: '0.5px solid var(--border)',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{tableName} · {r.customer_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {timeLabel} · {r.party_size} os.{r.customer_phone ? ` · 📞 ${r.customer_phone}` : ''}
                    </div>
                    {r.notes && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, fontStyle: 'italic' }}>„{r.notes}"</div>
                    )}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 500, color: minutesTo <= 0 ? '#A32D2D' : '#185FA5', minWidth: 56, textAlign: 'right' }}>
                    {minutesTo <= 0 ? 'teraz' : `za ${minutesTo} min`}
                  </span>
                  <button
                    onClick={() => markReservationArrived(r)}
                    style={{
                      padding: '8px 16px', background: '#EAF3DE',
                      border: '0.5px solid #C0DD97', borderRadius: 8,
                      fontSize: 13, fontWeight: 500, color: '#3B6D11', cursor: 'pointer',
                    }}
                  >
                    Przyszedł
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
